import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '../../../components/feature/Layout';
import { buildCanonicalHref } from '../../../seo/buildCanonicalHref';
import {
  isServiceSlug,
  serviceContentKey,
  serviceSummaryKey,
  serviceTitleKey,
  type ServiceSlug,
} from '../../../data/services';
import ServiceHeader from './components/ServiceHeader';
import ServiceContent from './components/ServiceContent';
import { useTranslation } from 'react-i18next';

export default function ServiceDetailPage() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!slug || !isServiceSlug(slug)) {
      navigate('/services', { replace: true });
    }
  }, [slug, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!slug || !isServiceSlug(slug)) {
    return null;
  }

  const serviceSlug = slug as ServiceSlug;
  const title = t(serviceTitleKey(serviceSlug));
  const summary = t(serviceSummaryKey(serviceSlug));
  const content = t(serviceContentKey(serviceSlug));

  const brand = t('seo.brand');
  const documentTitle = `${title} · ${brand}`;
  const canonical = buildCanonicalHref(pathname);
  const keywordsContent = t(`seo.services.${serviceSlug}.keywords`);

  return (
    <Layout>
      <Helmet prioritizeSeoTags>
        <title>{documentTitle}</title>
        <meta name="description" content={summary} />
        {keywordsContent ? <meta name="keywords" content={keywordsContent} /> : null}
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={summary} />
        {canonical ? <meta property="og:url" content={canonical} /> : null}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={summary} />
      </Helmet>

      <ServiceHeader title={title} summary={summary} />
      <ServiceContent content={content} />

      <section className="bg-dark-card border-t border-dark-border py-16">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-sm text-slate-500 mb-1">{t('serviceDetail.cta.subtitle')}</p>
            <p className="text-xl font-heading font-bold text-white">{t('serviceDetail.cta.title')}</p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 bg-primary text-dark-bg font-semibold text-sm rounded-xl hover:bg-primary-light transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-customer-service-line text-sm" />
              </div>
              {t('nav.cta')}
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-3 bg-dark-bg border border-dark-border text-slate-400 hover:text-white hover:border-primary/30 font-medium text-sm rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm" />
              </div>
              {t('serviceDetail.backToList')}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
